import React, { useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import Link from 'next/link';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Markdown from '@components/Shared/Markdown/Markdown';
import ScrollUpButtonContainer from '@components/Shared/ScrollUpButton/ScrollUpButtonContainer';

import ChevronLftSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/chevron-lft.svg';
import ChevronDwnSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/chevron-dwn.svg';
import HomeCardDetailed from '@components/Shared/HomeCards/Detailed/HomeCardDetailed';
import LoadingIndicator from '@components/Shared/LoadingIndicator/LoadingIndicator';
import Button from '@components/Shared/Buttons/Button';
import {
  buildQueryString,
  getQueryParams,
  getQueryString,
} from 'pubweb-smokey/dist/utils/utils';
import { freezeScrollForUpdate } from '@components/Shared/ScrollContext/ScrollContext';
import { runCommonValidation } from 'pubweb-smokey/dist/utils/validation';
import Switch from '@components/Shared/Switch/Switch';
import useHistoryState from 'pubweb-smokey/dist/hooks/useHistoryState';
import { getSiteUrl } from '@utils/config';
import CTA from '@components/Shared/CTA/CTA';
import { extractColors, scrollToElement, formatHttpsUrl } from '@utils/utils';
import { DXFavoritesContext } from '@contexts/DXFavoritesContext';
import { LocationsContext } from '@contexts/LocationsContext';
import { getCuratedCollection } from '@services/contentfulService';
import { getModelsByModelNumbers } from '@services/homesService';

import AreaHeader from '@components/Shared/AreaHeader/AreaHeader';

import CuratedButtonGroup from '@components/CuratedCollections/CuratedButtonGroup';

import { CuratedCollectionsStyles } from './CuratedCollections.styled';

const Modal = dynamic(() => import('@components/Shared/Modal/Modal'), {
  ssr: false,
});

const Form = dynamic(() => import('@components/Shared/Form/Form'), {
  ssr: false,
});

const CityLocationSuggestion = dynamic(
  () => import('@components/Shared/CityLocationList/CityLocationSuggestion'),
  {
    ssr: false,
  }
);

// We always want to end a page with a CTA.
// We want to show 6 items on each full page (i.e. 5 homes followed by 1 CTA for a total of 6 items).
// For partial pages, we want to always end with a CTA
const HOMES_PER_VIEW = 6;
const DEFAULT_DISTANCE = 50;

const modelNumbersBatchSize = 25;

const listOfModelNumbersBatches = (curatedHomes) => {
  return curatedHomes.reduce((resultArray, item, index) => {
    const batchNumber = Math.floor(index / modelNumbersBatchSize);

    if (!resultArray[batchNumber]) {
      resultArray[batchNumber] = [];
    }

    resultArray[batchNumber].push(item);

    return resultArray;
  }, []);
};

const CuratedCollections = ({ curatedCollection }) => {
  const favoritesContext = useContext(DXFavoritesContext);
  const locationsContext = useContext(LocationsContext);
  const router = useRouter();
  const [formErrors, setFormErrors] = useState({});
  const [homesViewCount, setHomesViewCount] = useHistoryState(
    HOMES_PER_VIEW,
    'homesView'
  );
  const [updating, setUpdating] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [filterUnavailable, setFilterUnavailable] = useState(true);
  const [filteredHomes, setFilteredHomes] = useState([]);
  const [curatedHomes, setCuratedHomes] = useState(
    curatedCollection.curatedHomes || []
  );
  const [disclaimerText, setDisclaimerText] = useState('');
  const showDisclaimer = disclaimerText && disclaimerText !== '';
  const disclaimerRef = useRef();

  const buildHomeCards = (homeList) => {
    let ctaIndex = 4;
    let lastCtaIndex = 1;
    let ctaCount = 0;

    const homeCards = [];

    homeList.forEach((home, i) => {
      // Determine if a CTA needs to be added.
      const { model } = home;
      let renderCta = false;

      if (i === homeList.length - 1) {
        if (i - lastCtaIndex >= 2) {
          renderCta = true;
          lastCtaIndex = i;
        }
      } else if (i === ctaIndex) {
        renderCta = true;
        lastCtaIndex = ctaIndex;
        ctaIndex += HOMES_PER_VIEW - 1;
      }

      if (
        renderCta &&
        curatedCollection.promoCtas &&
        curatedCollection.promoCtas.length > 0
      ) {
        const cta = curatedCollection.promoCtas[ctaCount++];
        if (cta) {
          homeCards.push(
            <CTA
              customCategory={cta.ctaCustomCategory}
              headingText={cta.ctaHeader}
              buttonLocation={cta.ctaButtonLocation}
              buttonColors={extractColors(cta.buttonAndTextColors)}
              buttonText={cta.firstCtaButtonText}
              buttonUrl={cta.firstCtaButtonUrl}
              isPreFooterCTA={false}
              buttonAutomationId="cta-button-1"
              backgroundColor={extractColors(cta.ctaBackgroundColor)}
              image={cta.ctaImage !== null ? cta.ctaImage.file.url : null}
              imageAlign={cta.ctaImageLocation}
              contentAlign={cta.alignCtaContent}
              buttonOpensToNewTab={false}
              secondButtonColors={cta.secondButtonColor}
              button2Text={cta.secondCtaButtonText}
              button2Url={cta.secondCtaButtonUrl}
              button2AutomationId="cta-button-2"
            />
          );
        }

        if (ctaCount >= curatedCollection.promoCtas.length) {
          ctaCount = 0;
        }
      }

      homeCards.push(
        <HomeCardDetailed
          showStockIndicator={!updating}
          showUnavailableIndicator={!updating && model.dealers.length == 0}
          key={`home-${model.modelNumber}-${i}`}
          showPricing={false}
          nextJSLink={'/homes/' + model.modelNumber + '/'}
          linkComponent={Link}
          desktopOrientation="horizontal"
          handleCtaClick={() => {
            setShowFormModal(true); // eslint-disable-line no-undef
            setModelInfo(model); // eslint-disable-line no-undef
          }}
          handleFavoriteClick={() =>
            favoritesContext.actions.toggleFavoriteModel(model.modelNumber)
          }
          image={
            <Image
              alt={home.cardImage.description || model.modelDescription}
              src={formatHttpsUrl(home.cardImage.url + '?w=575&fm=webp')}
              width={575}
              height={383}
            />
          }
          isFavorited={
            !!favoritesContext.state.favoriteModels?.filter(
              (faveModels) => faveModels.modelNumber === home.modelNumber
            )[0]
          }
          showFavoritingHeart={true}
          homeObject={{
            numberInStock: model.dealers.filter((d) => d.hasModelInStock)
              .length,
            ...model,
          }}
        />
      );
    });

    return homeCards;
  };

  const [displayedCards, setDisplayedCards] = useState(
    buildHomeCards(curatedCollection.curatedHomes)
  );

  useEffect(() => {
    const queryParams = getQueryParams();
    if (queryParams.zip && locationsContext.state) {
      if (locationsContext.state.postalCode !== queryParams.zip) {
        locationsContext.actions.updatePostalCode(queryParams.zip);
      }
    }
  }, []);

  useEffect(() => {
    if (filterUnavailable) {
      setDisplayedCards(buildHomeCards(filteredHomes));
    } else {
      setDisplayedCards(buildHomeCards(curatedHomes));
    }
  }, [filterUnavailable, filteredHomes, favoritesContext]);

  useEffect(() => {
    if (locationsContext.state.postalCode) {
      let queryString = buildQueryString({
        zip: locationsContext.state.postalCode,
      });

      // strip the '?' from the beginning of the query string if it is present
      if (queryString.indexOf('?') === 0) {
        queryString = queryString.substring(queryString.indexOf('?') + 1);
      }

      if (queryString !== getQueryString(router.pathname)) {
        router.replace(
          `/curated-collections/${router.query.curatedCollection}?${queryString}`
        );
      }
    }
  }, [locationsContext.state.postalCode]);

  useEffect(() => {
    if (
      locationsContext.state.geocode &&
      locationsContext.state.geocode.latitude &&
      locationsContext.state.geocode.longitude
    ) {
      if (!updating && locationsContext.state.postalCode) {
        setFilterUnavailable(true);
      }

      if (curatedCollection?.curatedHomes?.length > 0) {
        setUpdating(true);
        let modelsByModelNumbersList = [];
        const fetchModels = async () => {
          for (const batch of listOfModelNumbersBatches(
            curatedCollection.curatedHomes
          )) {
            await getModelsByModelNumbers(
              batch.map((home) => home.modelNumber),
              locationsContext.state.geocode.latitude,
              locationsContext.state.geocode.longitude,
              DEFAULT_DISTANCE
            ).then((models) => {
              modelsByModelNumbersList =
                modelsByModelNumbersList.concat(models);
            });
          }
        };

        fetchModels()
          .then(() => {
            setUpdating(false);
            curatedCollection.curatedHomes.forEach((home) => {
              // This updates the curatedCollection.curatedHomes with new information (e.g. dealers, etc.), but it does not seem to persist
              // outside this effect. So these unfiltered homes are saved in setCuratedHomes state below.

              // eslint-disable-next-line prefer-destructuring
              home.model = modelsByModelNumbersList.filter(
                (m) =>
                  home.modelNumber.toLowerCase() == m.modelNumber.toLowerCase()
              )[0];
            });

            var c = curatedCollection.curatedHomes.filter((home) => {
              return home.model?.dealers.length > 0;
            });
            c.postalCode = locationsContext.state.postalCode;
            setCuratedHomes(curatedCollection.curatedHomes);
            setDisclaimerText(curatedCollection?.disclaimer);
            setFilteredHomes(c);
          })
          .catch((e) => console.log(e));
      }
    }
  }, [locationsContext.state.geocode, curatedCollection.slug]);

  return (
    <CuratedCollectionsStyles>
      <Head htmlAttributes={{ lang: 'en' }}>
        <title>{curatedCollection.titleTag}</title>
        <meta name="description" content={curatedCollection.metaDescription} />
        <link
          rel="canonical"
          href={`${getSiteUrl(router.asPath.split('?')[0])}`}
        />
        <meta
          property="og:image"
          content={
            (curatedCollection.ogImage && curatedCollection.ogImage.url) ||
            getSiteUrl('/public-assets/images/clayton-logo.jpg')
          }
        />
      </Head>
      <AreaHeader
        breadCrumbs={[
          {
            text: (
              <span className="find-a-home-link">
                <div>
                  <ChevronLftSvg />
                </div>{' '}
                View Homes
              </span>
            ),
            link: '/find-a-home',
          },
        ]}
      />

      <ScrollUpButtonContainer />

      <div className="constrained-width">
        <div className="collection-header">
          <Image
            src={formatHttpsUrl(curatedCollection.collectionIcon.url)}
            alt={
              curatedCollection.collectionIcon.description ||
              curatedCollection.collectionIcon.title
            }
            width={61}
            height={61}
          />
          <h1>{curatedCollection.collectionTitle}</h1>
          <p>{curatedCollection.collectionDescription}</p>
          {showDisclaimer && (
            <a
              className="caption"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                scrollToElement(disclaimerRef.current);
              }}
            >
              <br />
              See below for complete pricing details
            </a>
          )}
        </div>
        <div className="collection-location">
          {!updating && (
            <div className="collection-location-indicator">
              <Switch
                className="gtm-location-toggle"
                name="filter-unavailable"
                label="Show homes available near"
                checked={filterUnavailable}
                onChange={(e) => {
                  setFilterUnavailable(e.target.checked);
                }}
              />
              <a
                href="#"
                className="zip-current label large"
                onClick={(e) => {
                  e.preventDefault();
                  setShowLocationModal(!showLocationModal);
                }}
              >
                &nbsp;{locationsContext.state.postalCode}{' '}
                <div>
                  <ChevronDwnSvg />
                </div>
              </a>
            </div>
          )}
          {updating && (
            <div className="collection-location-updating">
              <span className="label large">Checking home availability</span>
              <LoadingIndicator width="64" height="64" />
            </div>
          )}
          {showLocationModal && (
            <Modal
              onClose={() => {
                setShowLocationModal(false);
                setFormErrors({});
              }}
            >
              <Form
                onFormValidate={(formState) => {
                  const errors = runCommonValidation(formState);
                  setFormErrors(errors);
                  return errors.hasErrors;
                }}
                onSubmit={(formData) => {
                  locationsContext.actions.updatePostalCode(formData.zip);
                  setShowLocationModal(false);
                }}
              >
                <div className="location-form">
                  <CityLocationSuggestion
                    dropDownStyle="compact"
                    id="zip"
                    errorMessage={formErrors.zip}
                  />
                </div>
                <div className="location-controls">
                  <Button isCompact={true}>Apply</Button>
                </div>
              </Form>
            </Modal>
          )}
        </div>
        {!updating && displayedCards.length == 0 && (
          <div className="curated-collection-empty">
            <h3>None of the curated homes are available in your location</h3>
            <p className="intro">
              {' '}
              Please turn off the location toggle or change your location.
            </p>
            <div className="illustration-container">
              <Image
                src="/public-assets/images/no-homes-illustration.svg"
                alt="No Homes Illustration"
                fill
              />
            </div>
          </div>
        )}
        {!updating &&
          displayedCards.length > 0 &&
          displayedCards.slice(0, homesViewCount)}

        {!updating && homesViewCount < displayedCards.length && (
          <Button
            fillContainer={true}
            buttonStyle="outlined"
            isCompact={false}
            className="load-more"
            onClick={() => {
              freezeScrollForUpdate();
              setHomesViewCount(homesViewCount + HOMES_PER_VIEW);
            }}
          >
            LOAD MORE
          </Button>
        )}
      </div>
      {!updating && showDisclaimer && (
        <div className="disclaimer" ref={disclaimerRef}>
          <a name="disclaimer" className="constrained-width caption">
            <Markdown markdown={disclaimerText} />
          </a>
        </div>
      )}
      {curatedCollection.additionalCollections && (
        <CuratedButtonGroup
          heading="More Collections"
          leftJustify={false}
          buttonInfo={curatedCollection.additionalCollections}
        />
      )}
    </CuratedCollectionsStyles>
  );
};

CuratedCollections.propTypes = {
  curatedCollection: PropTypes.shape({
    additionalCollections: PropTypes.any,
    collectionDescription: PropTypes.any,
    collectionIcon: PropTypes.shape({
      description: PropTypes.any,
      title: PropTypes.any,
      url: PropTypes.any,
    }),
    collectionTitle: PropTypes.any,
    curatedHomes: PropTypes.array,
    disclaimer: PropTypes.string,
    metaDescription: PropTypes.any,
    ogImage: PropTypes.shape({
      url: PropTypes.any,
    }),
    promoCtas: PropTypes.array,
    slug: PropTypes.string,
    titleTag: PropTypes.any,
  }),
};

export async function getServerSideProps({ params }) {
  let curatedCollection = null;

  await getCuratedCollection('/curated-collections/' + params.curatedCollection)
    .then((result) => {
      curatedCollection = result;
    })
    .catch((e) => {
      console.log(e);
    });

  if (!curatedCollection) {
    return {
      notFound: true,
    };
  }

  let modelsByModelNumbersList = [];
  for (const batch of listOfModelNumbersBatches(
    curatedCollection.curatedHomes
  )) {
    await getModelsByModelNumbers(batch.map((home) => home.modelNumber)).then(
      (models) => {
        modelsByModelNumbersList = modelsByModelNumbersList.concat(models);
      }
    );
  }
  curatedCollection.curatedHomes.forEach((home) => {
    // eslint-disable-next-line prefer-destructuring
    home.model = modelsByModelNumbersList.filter(
      (m) => home.modelNumber.toLowerCase() == m.modelNumber.toLowerCase()
    )[0];
  });

  curatedCollection.curatedHomes = curatedCollection.curatedHomes.filter(
    (home) => {
      return !!home.model;
    }
  );

  return {
    props: { curatedCollection },
  };
}

export default CuratedCollections;
